import * as v from 'valibot'
import type { PayRateType } from './jobs'

/**
 * The money ledger's words: what actually moved, as the studio's own
 * bookkeeping. No payment is processed here — the ledger records what was
 * handed over, against the rates and hours that say what is owed. Rows are
 * append-only: a mistake is voided with its reason and recorded again,
 * never edited.
 *
 * Direction is the ledger's first fact: `inbound` is money the studio
 * received (from a client, against a job), `outbound` is money the studio
 * paid (to a player, against their work on a job).
 */

export const PAYMENT_DIRECTIONS = ['inbound', 'outbound'] as const
export type PaymentDirection = (typeof PAYMENT_DIRECTIONS)[number]

/** One movement of money, with the names joined for display — the API owns the joins. */
export interface PaymentTransaction {
  id: string
  direction: PaymentDirection
  jobId: string | null
  assignmentId: string | null
  clientId: string | null
  playerId: string | null
  jobTitle: string | null
  /** The client's name; when the client was deleted, the name they had. */
  clientName: string | null
  /** The client was deleted; the row kept their name as history. */
  clientDeleted: boolean
  playerName: string | null
  /** Who recorded it, and — when retracted — who voided it. */
  recordedBy: string
  recordedByName: string | null
  createdAt: string
  voidedAt: string | null
  voidedByName: string | null
  voidReason: string | null
  /** Minor units, always positive; the direction carries the sign. */
  amount: number
  currency: string
  method: string | null
  referenceNumber: string | null
  /** The day the money moved, not the day the row was written. */
  paymentDate: string
  notes: string | null
}

/**
 * The ledger's answer to "where does the job stand": live rows only, summed
 * per currency, derived at read time so the number cannot drift from the
 * rows it came from.
 */
export interface MoneyTotals {
  currency: string
  inboundMinor: number
  outboundMinor: number
}

/** A reference must name something that exists; emptiness is refused. */
const ref = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(64))

const currency = v.pipe(
  v.string(),
  v.trim(),
  v.transform((value) => value.toUpperCase()),
  v.regex(/^[A-Z]{3}$/, 'Use a three-letter currency code, like BDT or USD.'),
)

const paymentMethod = v.union([v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(120)), v.null()])
const paymentNotes = v.union([v.pipe(v.string(), v.trim(), v.maxLength(2000)), v.null()])

export const createPaymentSchema = v.pipe(
  v.object({
    direction: v.picklist(PAYMENT_DIRECTIONS),
    amount: v.pipe(
      v.number(),
      v.integer('Money is whole minor units — send cents, not decimals.'),
      v.minValue(1, 'An amount of nothing is not a payment.'),
      v.maxValue(100_000_000, 'That amount is beyond the ledger’s imagination.'),
    ),
    currency: v.optional(currency),
    jobId: v.optional(ref),
    assignmentId: v.optional(ref),
    clientId: v.optional(ref),
    playerId: v.optional(ref),
    method: v.optional(paymentMethod),
    referenceNumber: v.optional(v.union([v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(120)), v.null()])),
    paymentDate: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(10)),
    notes: v.optional(paymentNotes),
  }),
  v.check(
    (input) => [input.jobId, input.assignmentId, input.clientId, input.playerId].some((subject) => subject !== undefined),
    'Say what the money is for — a job, a client, or a player.',
  ),
)

export type CreatePaymentInput = v.InferOutput<typeof createPaymentSchema>

/** Voiding is a decision with a reason; the reason rides the record forever. */
export const voidPaymentSchema = v.object({
  reason: v.pipe(v.string(), v.trim(), v.minLength(3, 'Say why, in at least a few letters.'), v.maxLength(500)),
})

export type VoidPaymentInput = v.InferOutput<typeof voidPaymentSchema>

/** A query for the trail: one subject's rows, or the recent ledger. */
export interface PaymentFilter {
  jobId?: string
  assignmentId?: string
  clientId?: string
  playerId?: string
  limit?: number
}

/** What a rate looks like when a surface is allowed to show it: the basis and the money. */
export interface RateView {
  type: PayRateType
  amount: number
  currency: string
}
